import React from 'react';
import { ArrowLeft, Workflow, Zap, Clock, TrendingUp, CheckCircle, Star, ArrowRight, Bot, Link, BarChart3, Settings } from 'lucide-react';

interface N8NAutomationPageProps {
  onBack: () => void;
}

const N8NAutomationPage: React.FC<N8NAutomationPageProps> = ({ onBack }) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: Clock,
      title: "Ahorro de Tiempo",
      description: "Automatiza tareas repetitivas y libera a tu equipo para actividades de mayor valor."
    },
    {
      icon: TrendingUp,
      title: "Escalabilidad",
      description: "Procesos que crecen con tu negocio sin necesidad de contratar más personal."
    },
    {
      icon: Link,
      title: "Integración Total",
      description: "Conecta todas tus herramientas y sistemas en flujos de trabajo unificados."
    },
    {
      icon: BarChart3,
      title: "Análisis en Tiempo Real",
      description: "Monitoreo y métricas detalladas de todos tus procesos automatizados."
    }
  ];

  const integrations = [
    {
      name: "CRM Systems",
      description: "Salesforce, HubSpot, Pipedrive",
      logo: "👥"
    },
    {
      name: "Email Marketing",
      description: "Mailchimp, SendGrid, Constant Contact",
      logo: "📧"
    },
    {
      name: "E-commerce",
      description: "Shopify, WooCommerce, Magento",
      logo: "🛒"
    },
    {
      name: "Social Media",
      description: "Facebook, Twitter, LinkedIn, Instagram",
      logo: "📱"
    },
    {
      name: "Cloud Storage",
      description: "Google Drive, Dropbox, OneDrive",
      logo: "☁️"
    },
    {
      name: "Databases",
      description: "MySQL, PostgreSQL, MongoDB",
      logo: "🗄️"
    }
  ];

  
  const process = [
    {
      step: "01",
      title: "Análisis de Procesos",
      description: "Identificamos y mapeamos todos los procesos manuales que pueden ser automatizados."
    },
    {
      step: "02",
      title: "Diseño de Flujos",
      description: "Creamos diagramas detallados de los workflows automatizados con todas las condiciones."
    },
    {
      step: "03",
      title: "Configuración N8N",
      description: "Implementamos los flujos en N8N con todas las integraciones y validaciones necesarias."
    },
    {
      step: "04",
      title: "Pruebas y Validación",
      description: "Realizamos pruebas exhaustivas con datos reales para garantizar funcionamiento perfecto."
    },
    {
      step: "05",
      title: "Monitoreo y Optimización",
      description: "Supervisamos el rendimiento y optimizamos continuamente los procesos automatizados."
    }
  ];

  const benefits = [
    { metric: "80%", description: "Reducción en tiempo de procesamiento" },
    { metric: "95%", description: "Menos errores humanos" },
    { metric: "24/7", description: "Operación continua sin intervención" },
    { metric: "300%", description: "Aumento en productividad del equipo" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver a Servicios</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <img
                  src="/src/assets/logoModelos2.png"
                  alt="ModeloSmart Logo"
                  style={{ height: "120px", width: "auto" }}
                />
              </div>{" "}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center">
                    <Workflow className="h-8 w-8 text-orange-600" />
                  </div>
                  <h1 className="text-5xl font-bold text-gray-900">
                    Automatización con <span className="text-orange-600">N8N</span>
                  </h1>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Transformamos tus procesos manuales en flujos automatizados inteligentes que 
                  trabajan 24/7, reducen errores y liberan a tu equipo para tareas estratégicas.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="inline-flex items-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Automatizar Procesos
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>

              </div>

              {/* Benefits Stats */}
              <div className="grid grid-cols-2 gap-6 pt-4">
                {benefits.slice(0, 2).map((benefit, index) => (
                  <div key={index} className="text-center p-4 bg-orange-50 rounded-xl">
                    <div className="text-2xl font-bold text-orange-600 mb-1">{benefit.metric}</div>
                    <div className="text-sm text-gray-600">{benefit.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Automatización de procesos con N8N"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-orange-600/20 to-transparent rounded-2xl"></div>
              
              {/* Floating elements */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-2">
                  <Bot className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium">Automatización activa</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Beneficios de la <span className="text-orange-600">Automatización</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              La automatización con N8N transforma la manera en que tu empresa opera, 
              generando eficiencias que impactan directamente en tu rentabilidad.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Additional Benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border border-orange-100">
                <div className="text-3xl font-bold text-orange-600 mb-2">{benefit.metric}</div>
                <div className="text-gray-700 font-medium">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Integraciones <span className="text-orange-600">Disponibles</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              N8N se conecta con más de 400 aplicaciones y servicios. Integramos todas 
              las herramientas que ya usas en flujos automatizados inteligentes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-4xl mb-4">{integration.logo}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{integration.name}</h3>
                <p className="text-gray-600">{integration.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-orange-600">Proceso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Metodología probada para identificar, diseñar e implementar automatizaciones 
              que generen el máximo impacto en tu organización.
            </p>
          </div>

          <div className="relative">
            {/* Process line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-600 to-red-600 hidden lg:block"></div>
            
            <div className="space-y-12">
              {process.map((step, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="hidden lg:flex w-16 h-16 bg-orange-600 rounded-full items-center justify-center text-white font-bold text-lg mr-8 flex-shrink-0 shadow-lg">
                    {step.step}
                  </div>
                  
                  <div className="bg-white p-8 rounded-2xl flex-1 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contacto" className="py-20 bg-gradient-to-br from-orange-900 via-orange-800 to-red-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              ¿Listo para <span className="text-yellow-400">automatizar tu negocio</span>?
            </h2>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Identifiquemos juntos los procesos que pueden ser automatizados para generar 
              ahorros inmediatos en tu operación.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 bg-white text-orange-900 font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-2xl"
              >
                Solicitar Análisis Gratuito
                <ArrowRight className="ml-3 h-6 w-6" />
              </button>
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 border-2 border-white/50 text-white font-semibold text-lg rounded-xl hover:bg-white/10 hover:border-white transition-all duration-200"
              >
                Ver Demo en Vivo
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default N8NAutomationPage;